import React, { useCallback, useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Users,
  Search,
  Plus,
  Edit,
  Trash2,
  Eye,
  Briefcase,
  Building2,
  BarChart2,
  UserCheck,
  Clock,
  History,
  RotateCcw,
  XCircle,
  User,
} from "lucide-react";

import Loader from "../../../loader/Loader";
import AddEmployee from "./AddEmployee";
import ViewEmployeeModal from "./ViewEmployeeModal";
import AuditInfoModal, { buildAuditRows } from "../../../shared/AuditInfoModal";
import Pagination from "../../Pagination";
import { domain } from "../../../../security";
import { selectUserID } from "../../../../redux/IchthusSlice";
import { format } from "date-fns";

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return format(new Date(dateString), "MMM dd, yyyy");
};

// ==========================================
// HISTORY MODAL COMPONENT
// ==========================================
const DeletedHistoryModal = ({ isOpen, onClose, onRestore }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [auditItem, setAuditItem] = useState(null);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${domain}/api/Employees/history?search=${search}`,
      );
      setHistoryData(response.data);
    } catch {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
      setCurrentPage(1);
    }
  }, [isOpen, fetchHistory]);

  if (!isOpen) return null;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = historyData.slice(indexOfFirstItem, indexOfLastItem);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-white">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <div className="p-2 bg-red-50 rounded-full text-red-500">
              <Trash2 size={18} />
            </div>
            Deleted Employees History
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition"
          >
            <XCircle size={22} />
          </button>
        </div>

        <div className="px-4 py-3 bg-slate-50/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search deleted employees..."
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm outline-none"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 bg-slate-50/50">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader />
            </div>
          ) : historyData.length === 0 ? (
            <div className="text-center py-12 text-slate-500 text-sm">
              No deleted records found.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="hidden sm:block bg-white rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full text-sm text-left text-slate-600">
                  <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                    <tr>
                      <th className="px-4 py-3">Employee Name</th>
                      <th className="px-4 py-3">Deleted By</th>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {currentItems.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-800 flex items-center gap-1">
                          {item.firstName} {item.lastName}
                          <button
                            onClick={() => setAuditItem(item)}
                            className="text-amber-500 hover:text-amber-700 ml-1"
                          >
                            <Clock size={14} />
                          </button>
                        </td>
                        <td className="px-4 py-3 text-xs font-medium text-red-500">
                          {item.deletedByFullName || "Unknown"}
                        </td>
                        <td className="px-4 py-3 text-red-500 text-xs">
                          {formatDate(item.deletedDate)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={async () => {
                              await onRestore(item.id);
                              fetchHistory();
                            }}
                            className="text-green-600 hover:text-green-700 font-semibold text-xs flex items-center justify-end gap-1 ml-auto"
                          >
                            <RotateCcw size={14} /> Restore
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                itemsPerPage={itemsPerPage}
                totalItems={historyData.length}
                currentPage={currentPage}
                paginate={paginate}
              />
            </div>
          )}
        </div>
      </div>
      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={`${auditItem?.firstName} ${auditItem?.lastName}`}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================
const AllEmployees = () => {
  const currentUserID = useSelector(selectUserID);
  const [employeeData, setEmployeeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [employeeToEdit, setEmployeeToEdit] = useState(null);
  const [employeeToView, setEmployeeToView] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [auditItem, setAuditItem] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [employeesPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredEmployees, setFilteredEmployees] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${domain}/api/Employees`);
      setEmployeeData(response.data);
    } catch (error) {
      toast.error("Failed to fetch Employees.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const results = employeeData.filter((employee) => {
      const fullName =
        `${employee.firstName || ""} ${employee.middleName || ""} ${employee.lastName || ""}`.toLowerCase();
      return fullName.includes(searchTerm.toLowerCase());
    });
    setFilteredEmployees(results);
    setCurrentPage(1);
  }, [searchTerm, employeeData]);

  const handleSoftDelete = async (id) => {
    if (!window.confirm("Move this employee to history?")) return;
    try {
      await axios.put(`${domain}/api/Employees/soft-delete/${id}`, {
        userID: currentUserID,
      });
      toast.success("Employee moved to history!");
      fetchData();
    } catch {
      toast.error("Failed to delete employee.");
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.put(`${domain}/api/Employees/restore/${id}`, {
        userID: currentUserID,
      });
      toast.success("Employee restored!");
      fetchData();
    } catch {
      toast.error("Failed to restore employee.");
    }
  };

  const openModal = async (employee = null) => {
    if (!employee) {
      setEmployeeToEdit(null);
      setIsModalVisible(true);
      return;
    }
    setIsDetailLoading(true);
    try {
      const response = await axios.get(
        `${domain}/api/Employees/${employee.id}`,
      );
      setEmployeeToEdit(response.data);
      setIsModalVisible(true);
    } catch {
      toast.error("Failed to fetch details.");
    } finally {
      setIsDetailLoading(false);
    }
  };

  const openViewModal = async (employee) => {
    setIsDetailLoading(true);
    try {
      const response = await axios.get(
        `${domain}/api/Employees/${employee.id}`,
      );
      setEmployeeToView(response.data);
    } catch {
      toast.error("Failed to fetch details.");
    } finally {
      setIsDetailLoading(false);
    }
  };

  const indexOfLastEmployee = currentPage * employeesPerPage;
  const indexOfFirstEmployee = indexOfLastEmployee - employeesPerPage;
  const currentEmployees = filteredEmployees.slice(
    indexOfFirstEmployee,
    indexOfLastEmployee,
  );
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="min-h-screen pb-12">
      <ToastContainer position="top-right" autoClose={3000} />
      {isDetailLoading && <Loader />}

      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-20">
        <div className="max-w-[95%] mx-auto px-4 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Users className="text-indigo-600" /> Employee Dashboard
            </h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setIsHistoryModalVisible(true)}
              className="flex items-center gap-2 bg-slate-100 text-slate-600 text-sm font-semibold px-4 py-2.5 rounded-lg hover:bg-slate-200 transition-all"
            >
              <History size={18} /> History
            </button>
            <button
              onClick={() => openModal()}
              className="flex items-center gap-2 bg-indigo-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg shadow-sm hover:bg-indigo-700 transition-all"
            >
              <Plus size={18} /> Add Employee
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[95%] mx-auto px-4 mt-6">
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by Employee Name..."
              className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader />
          </div>
        ) : (
          <>
            <div className="hidden md:block bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <table className="w-full text-sm text-left text-slate-600">
                <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b">
                  <tr>
                    <th className="px-6 py-4">Employee Name</th>
                    <th className="px-6 py-4">Position / Department</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {currentEmployees.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50 transition">
                      <td className="px-6 py-4 font-semibold text-slate-800">
                        {item.firstName} {item.lastName}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-slate-700 font-medium">
                          {item.position}
                        </div>
                        <div className="text-xs text-slate-400">
                          {item.department}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${item.employmentStatus === "Active" ? "bg-green-50 text-green-700 border-green-100" : "bg-red-50 text-red-700 border-red-100"}`}
                        >
                          {item.employmentStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setAuditItem(item)}
                            className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded transition"
                            title="Audit Info"
                          >
                            <Clock size={18} />
                          </button>
                          <button
                            onClick={() => openViewModal(item)}
                            className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded transition"
                            title="View"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            onClick={() => openModal(item)}
                            className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition"
                            title="Edit"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleSoftDelete(item.id)}
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className="md:hidden space-y-4">
              {currentEmployees.map((item) => (
                <div
                  key={item.id}
                  className="bg-white p-4 rounded-lg border border-slate-200"
                >
                  <div className="flex justify-between items-start mb-3">
                    <span className="font-bold text-slate-800">
                      {item.firstName} {item.lastName}
                    </span>
                    <span className="text-[10px] uppercase font-bold text-green-600">
                      {item.employmentStatus}
                    </span>
                  </div>
                  <div className="flex gap-2 pt-3 border-t">
                    <button
                      onClick={() => setAuditItem(item)}
                      className="flex-1 py-2 text-amber-600 text-xs font-medium flex justify-center items-center gap-1"
                    >
                      <Clock size={14} /> Audit
                    </button>
                    <button
                      onClick={() => openViewModal(item)}
                      className="flex-1 py-2 text-slate-600 text-xs font-medium flex justify-center items-center gap-1"
                    >
                      <Eye size={14} /> View
                    </button>
                    <button
                      onClick={() => openModal(item)}
                      className="flex-1 py-2 text-indigo-600 text-xs font-medium flex justify-center items-center gap-1"
                    >
                      <Edit size={14} /> Edit
                    </button>
                    <button
                      onClick={() => handleSoftDelete(item.id)}
                      className="p-2 text-red-600"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <Pagination
                itemsPerPage={employeesPerPage}
                totalItems={filteredEmployees.length}
                currentPage={currentPage}
                paginate={paginate}
              />
            </div>
          </>
        )}
      </div>

      {isModalVisible && (
        <div className="fixed inset-0 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <AddEmployee
              onClose={() => {
                setIsModalVisible(false);
                fetchData();
              }}
              refreshData={fetchData}
              employeeToEdit={employeeToEdit}
            />
          </div>
        </div>
      )}

      {employeeToView && (
        <ViewEmployeeModal
          employee={employeeToView}
          onClose={() => setEmployeeToView(null)}
        />
      )}
      <DeletedHistoryModal
        isOpen={isHistoryModalVisible}
        onClose={() => {
          setIsHistoryModalVisible(false);
          fetchData();
        }}
        onRestore={handleRestore}
      />
      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={`${auditItem?.firstName} ${auditItem?.lastName}`}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

export default AllEmployees;
